/* MT page section nav — fixed pin + anchor scroll */
(function () {
  'use strict';

  function parsePx(value) {
    var n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }

  function resolveTarget(anchor) {
    if (!anchor) return null;
    var value = anchor.trim().replace(/^#/, '');
    if (!value) return null;

    if (/^[#.\[]/.test(anchor.trim()) || value.includes(' ')) {
      try {
        var selector = anchor.trim();
        if (!/^[#.\[]/.test(selector)) selector = '#' + selector;
        return document.querySelector(selector);
      } catch (e) {
        return null;
      }
    }

    var direct = document.getElementById(value) || document.getElementById('shopify-section-' + value);
    if (direct) return direct;

    var sections = document.querySelectorAll('[id^="shopify-section-"]');
    var i;
    for (i = 0; i < sections.length; i++) {
      if (sections[i].id.endsWith('__' + value)) return sections[i];
    }
    for (i = 0; i < sections.length; i++) {
      if (sections[i].id.includes(value)) return sections[i];
    }

    return null;
  }

  class MtPageNav extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.stickyEnabled = !this.hasAttribute('data-sticky-disabled');
      this.extraOffset = parseInt(this.dataset.scrollOffset, 10) || 0;
      this.links = Array.from(this.querySelectorAll('[data-page-nav-link]'));
      this.bar = this.querySelector('.mt-page-nav__bar');
      this.sentinel = this.querySelector('[data-page-nav-sentinel]');
      this.placeholder = this.querySelector('[data-page-nav-placeholder]');
      this.targets = [];
      this.isPinned = false;

      if (!this.bar) return;

      this.links.forEach(function (link) {
        var target = resolveTarget(link.dataset.anchor);
        if (target) {
          this.targets.push({ link: link, target: target });
        } else {
          link.closest('.mt-page-nav__item').hidden = true;
        }
      }, this);

      this.links.forEach(function (link) {
        if (!link.closest('.mt-page-nav__item').hidden) {
          link.addEventListener('click', this.onLinkClick.bind(this));
        }
      }, this);

      this.syncNavHeight();
      this._onResize = this.onResize.bind(this);
      this._onScroll = this.onScroll.bind(this);
      window.addEventListener('resize', this._onResize);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('load', this._onResize);

      if (window.ResizeObserver) {
        this._resizeObserver = new ResizeObserver(this._onResize);
        this._resizeObserver.observe(this.bar);
        var section = this.closest('.shopify-section');
        if (section && section.previousElementSibling) {
          this._resizeObserver.observe(section.previousElementSibling);
        }
      }

      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('load', this._onResize);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this.setPinned(false);
    }

    syncNavHeight() {
      if (!this.bar) return;
      document.documentElement.style.setProperty('--mt-page-nav-height', Math.ceil(this.bar.offsetHeight) + 'px');
    }

    getHeaderOffset() {
      var header = document.querySelector('mt-header');
      if (!header) return 0;

      var rect = header.getBoundingClientRect();
      if (rect.bottom <= 0) return 0;

      return Math.ceil(rect.height);
    }

    getPromoOffset() {
      var promo = document.querySelector('mt-promo-bar');
      if (!promo) {
        return parsePx(window.getComputedStyle(document.documentElement).getPropertyValue('--mt-promo-bar-offset'));
      }

      var rect = promo.getBoundingClientRect();
      if (rect.bottom <= 0) return 0;

      return Math.max(
        parsePx(window.getComputedStyle(document.documentElement).getPropertyValue('--mt-promo-bar-offset')),
        Math.ceil(rect.height)
      );
    }

    getStickyTop() {
      return this.getPromoOffset() + this.getHeaderOffset();
    }

    getNavHeight() {
      return this.bar ? this.bar.offsetHeight : 0;
    }

    getScrollOffset() {
      return this.getStickyTop() + this.getNavHeight() + this.extraOffset;
    }

    updateStickyTop() {
      var top = this.stickyEnabled ? this.getStickyTop() : 0;
      document.documentElement.style.setProperty('--mt-page-nav-top', top + 'px');
      if (this.isPinned && this.bar) {
        this.bar.style.top = top + 'px';
      }
    }

    shouldPin() {
      if (!this.stickyEnabled) return false;

      var stickyTop = this.getStickyTop();
      var marker = this.sentinel || this.bar;
      if (!marker) return false;

      return marker.getBoundingClientRect().top <= stickyTop + 0.5;
    }

    setPinned(pinned) {
      if (!this.stickyEnabled || pinned === this.isPinned || !this.bar) return;

      this.isPinned = pinned;
      this.bar.classList.toggle('is-fixed', pinned);
      this.bar.toggleAttribute('data-pinned', pinned);

      if (pinned) {
        var top = this.getStickyTop();
        this.bar.style.position = 'fixed';
        this.bar.style.top = top + 'px';
        this.bar.style.left = '0';
        this.bar.style.right = '0';
        this.bar.style.width = '100%';
        this.bar.style.zIndex = '28';
      } else {
        this.bar.style.position = '';
        this.bar.style.top = '';
        this.bar.style.left = '';
        this.bar.style.right = '';
        this.bar.style.width = '';
        this.bar.style.zIndex = '';
      }

      if (this.placeholder) {
        this.placeholder.hidden = !pinned;
      }

      this.syncNavHeight();
    }

    onResize() {
      this.syncNavHeight();
      this.updateStickyTop();
      this.onScroll();
    }

    onScroll() {
      this.updateStickyTop();
      this.setPinned(this.shouldPin());

      if (!this.targets.length) return;

      var offset = this.getScrollOffset();
      var active = this.targets[0].link;

      this.targets.forEach(function (item) {
        if (item.target.getBoundingClientRect().top - offset <= 32) {
          active = item.link;
        }
      });

      this.setActiveLink(active);
    }

    onLinkClick(event) {
      var link = event.currentTarget;
      var target = resolveTarget(link.dataset.anchor);
      if (!target) return;

      event.preventDefault();
      this.scrollToTarget(target);
      this.setActiveLink(link);
    }

    scrollToTarget(target) {
      this.updateStickyTop();
      var offset = this.getScrollOffset();
      var top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    setActiveLink(activeLink) {
      this.links.forEach(function (link) {
        link.classList.toggle('is-active', link === activeLink);
      });
    }
  }

  if (!customElements.get('mt-page-nav')) {
    customElements.define('mt-page-nav', MtPageNav);
  }
})();
